/**
 * ChatContextNotesContext
 * ──────────────────────
 * Shares a small "shopping cart" of Personal Legal Knowledge Base notes
 * that the user has flagged to inject as context for their next chat turn.
 *
 * The list is kept in memory and mirrored to localStorage so it survives
 * page reloads (e.g. user clicks "Use in chat" on /legal/knowledge then
 * navigates to /chat).
 *
 * Each entry is the minimum the chat composer needs to render the chip:
 *   { note_id, title, citation }
 *
 * Hard cap: 5 notes per request (must match backend KB_NOTES_MAX_PER_REQUEST).
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "neuracom_chat_kb_notes_v1";
export const MAX_KB_NOTES = 5;

const ChatContextNotesContext = createContext(null);

export function ChatContextNotesProvider({ children }) {
  const [notes, setNotes] = useState(() => {
    try {
      const raw = typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.slice(0, MAX_KB_NOTES);
    } catch (e) {
      return [];
    }
  });

  // Mirror to localStorage on every change so navigation across pages preserves state.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(notes));
    } catch (e) {
      // quota exceeded or disabled — fine, in-memory state still works
    }
  }, [notes]);

  const addNote = useCallback((note) => {
    if (!note || !note.note_id) return { added: false, reason: "invalid" };
    let result = { added: false, reason: "duplicate" };
    setNotes((cur) => {
      if (cur.some((n) => n.note_id === note.note_id)) {
        result = { added: false, reason: "duplicate" };
        return cur;
      }
      if (cur.length >= MAX_KB_NOTES) {
        result = { added: false, reason: "max" };
        return cur;
      }
      result = { added: true };
      return [...cur, {
        note_id: note.note_id,
        title: note.title,
        citation: note.citation || null,
      }];
    });
    return result;
  }, []);

  const removeNote = useCallback((note_id) => {
    setNotes((cur) => cur.filter((n) => n.note_id !== note_id));
  }, []);

  const clearNotes = useCallback(() => {
    setNotes([]);
  }, []);

  return (
    <ChatContextNotesContext.Provider
      value={{ notes, addNote, removeNote, clearNotes, max: MAX_KB_NOTES }}
    >
      {children}
    </ChatContextNotesContext.Provider>
  );
}

export function useChatContextNotes() {
  const ctx = useContext(ChatContextNotesContext);
  if (!ctx) throw new Error("useChatContextNotes must be used inside ChatContextNotesProvider");
  return ctx;
}
